import { createFileRoute, Link } from "@tanstack/react-router";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { SectionCta } from "@/components/site/SectionCta";
import { MedicalReviewBanner } from "@/components/site/MedicalReview";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";
import { imageRights, PHYSICIAN_REF } from "@/lib/medical-schema";
import {
  PROCEDURE_MEDIA,
} from "@/lib/procedure-media";
import pouchRevisionWebp800 from "@/assets/revision-hub-annotated-800.webp.asset.json";
import pouchRevisionWebp1200 from "@/assets/revision-hub-annotated-1200.webp.asset.json";
import pouchRevisionWebp1600 from "@/assets/revision-hub-annotated-1600.webp.asset.json";
import pouchRevisionJpg800 from "@/assets/revision-hub-annotated-800.jpg.asset.json";
import pouchRevisionJpg1200 from "@/assets/revision-hub-annotated-1200.jpg.asset.json";
import pouchRevisionJpg1600 from "@/assets/revision-hub-annotated-1600.jpg.asset.json";

const FAQS: { q: string; a: string }[] = [
  {
    q: "¿Por qué los pacientes recuperan peso después de la cirugía bariátrica?",
    a: "Casi siempre es cuestión de biología, no de fuerza de voluntad. Después de bajar de peso, el cuerpo eleva las hormonas del apetito, reduce las señales de saciedad y quema menos calorías en reposo. La operación original también puede cambiar con el tiempo: una manga puede dilatarse, una bolsa gástrica o su salida pueden agrandarse, y una banda puede deslizarse o erosionarse.",
  },
  {
    q: "¿Qué opciones de revisión existen después de un bypass gástrico?",
    a: "Principalmente tres. La reducción endoscópica de la salida (TORe) ajusta una conexión dilatada sin incisiones. La revisión quirúrgica reconstruye la bolsa gástrica. La distalización alarga el intestino delgado desviado para reforzar el efecto. Cuál conviene depende de su anatomía y sus metas.",
  },
  {
    q: "¿Qué opciones de revisión existen después de la manga gástrica?",
    a: "La manga se convierte con más frecuencia a un bypass gástrico en Y de Roux, que mejora el reflujo y agrega un efecto de malabsorción. Si necesita mayor pérdida de peso, un switch duodenal o un bypass duodenoileal de una sola anastomosis (SADI) pueden dar resultados mayores y más duraderos, con un seguimiento nutricional más estrecho.",
  },
  {
    q: "¿Qué se puede hacer si todavía tengo una banda gástrica?",
    a: "Las bandas ajustables más antiguas casi siempre se retiran y se convierten a una manga gástrica o a un bypass gástrico. Eso resuelve los problemas relacionados con la banda y da una pérdida de peso más confiable a largo plazo.",
  },
  {
    q: "¿Es segura la cirugía de revisión?",
    a: "Las revisiones tienen un riesgo un poco más alto que la cirugía inicial, porque se hacen sobre anatomía alterada y con tejido cicatricial. En manos con experiencia y en un centro acreditado, las tasas de complicaciones se mantienen bajas y el procedimiento es muy seguro.",
  },
  {
    q: "¿El seguro cubre la cirugía de revisión bariátrica?",
    a: "La mayoría de los planes cubren la revisión cuando es médicamente necesaria. La aprobación suele ser más fácil cuando hay una complicación mecánica o médica, como una banda deslizada, una estenosis o reflujo después de una manga, que en el caso de recuperación de peso por sí sola, que generalmente exige cumplir de nuevo los criterios actuales de IMC y salud del plan.",
  },
];

const REVISION_IMAGE = PROCEDURE_MEDIA.revisionHub;
const OG_IMAGE = `${SITE}${pouchRevisionJpg1200.url}`;
const OG_IMAGE_ALT = REVISION_IMAGE.alt.es;
const REVISION_IMAGE_CAPTION = REVISION_IMAGE.caption.es;
const REVISION_IMAGE_RIGHTS = imageRights(`${SITE}/es/cirugia-de-revision`);

const REVISION_IMAGE_OBJECT = {
  "@type": "ImageObject",
  url: OG_IMAGE,
  contentUrl: OG_IMAGE,
  caption: REVISION_IMAGE_CAPTION,
  width: 1200,
  height: 668,
  inLanguage: "es-US",
  ...REVISION_IMAGE_RIGHTS,
};

export const Route = createFileRoute("/es/cirugia-de-revision")({
  head: () => {
    const base = esHead("revisions");
    const meta = base.meta
      .filter((m) => !("property" in m && String(m.property).startsWith("og:image")))
      .filter((m) => !("name" in m && String(m.name).startsWith("twitter:image")))
      .concat([
        { property: "og:image", content: OG_IMAGE },
        { property: "og:image:alt", content: OG_IMAGE_ALT },
        { property: "og:image:type", content: "image/jpeg" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "668" },
        { name: "twitter:image", content: OG_IMAGE },
        { name: "twitter:image:alt", content: OG_IMAGE_ALT },
      ]);
    return {
      ...base,
      meta,
      scripts: import.meta.env.SSR ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                // Mirror the English page's MedicalWebPage + physician review.
                ...esWebPageSchema("revisions"),
                "@type": "MedicalWebPage",
                reviewedBy: PHYSICIAN_REF,
                mainEntity: { "@id": `${SITE}/es/cirugia-de-revision#procedure` },
                primaryImageOfPage: REVISION_IMAGE_OBJECT,
              },
              {
                "@type": "MedicalProcedure",
                "@id": `${SITE}/es/cirugia-de-revision#procedure`,
                mainEntityOfPage: { "@id": `${SITE}/es/cirugia-de-revision#webpage` },
                procedureType: "https://schema.org/SurgicalProcedure",
                name: "Cirugía de revisión bariátrica",
                alternateName: [
                  "Revisión de manga gástrica",
                  "Conversión de manga a bypass gástrico",
                  "Revisión de bypass gástrico",
                  "Retiro y conversión de banda gástrica",
                  "Cirugía bariátrica «redo»",
                ],
                description:
                  "La cirugía de revisión bariátrica corrige, modifica o convierte una operación previa para bajar de peso. Las opciones incluyen la revisión de la manga gástrica (conversión a bypass, re-manga o SADI/switch duodenal), la revisión del bypass gástrico con reducción transoral de la salida (TORe) o distalización, y el retiro o conversión de la banda gástrica.",
                url: `${SITE}/es/cirugia-de-revision`,
                image: REVISION_IMAGE_OBJECT,
              },
              REVISION_IMAGE_OBJECT,
              {
                "@type": "FAQPage",
                "@id": `${SITE}/es/cirugia-de-revision#faq`,
                inLanguage: "es-US",
                mainEntity: FAQS.map((f, i) => ({
                  "@type": "Question",
                  "@id": `${SITE}/es/cirugia-de-revision#faq-${i + 1}`,
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        },
      ] : [],
    };
  },
  component: EsRevisions,
});

function EsRevisions() {
  return (
    <EsPageShell
      page="revisions"
      title="Cirugía bariátrica de revisión: una segunda oportunidad para bajar de peso"
      intro="¿Tuvo cirugía para bajar de peso en el pasado y los resultados se desvanecieron o nunca llegaron por completo? No está solo. La obesidad es una enfermedad crónica con raíces biológicas profundas, y la cirugía de revisión es una parte común y establecida de la atención bariátrica moderna, no un último recurso ni una señal de fracaso personal."
      banner={<MedicalReviewBanner lang="es" className="mt-4 text-left" />}
    >
      <EsSection id="que-es" heading="¿Qué es la cirugía de revisión bariátrica?">
        <p>
          La cirugía de revisión, a veces llamada «redo», es cualquier procedimiento que corrige,
          modifica o convierte una operación bariátrica anterior para que sea más eficaz. Va desde un
          ajuste menor hasta una conversión completa de una operación a otra. El objetivo puede ser
          recuperar el efecto de pérdida de peso, aliviar un problema como el reflujo grave, o ambas
          cosas. Cada paciente llega con una anatomía distinta, una operación previa distinta y metas
          distintas, así que no existe un solo procedimiento de revisión: existe un menú de opciones.
          El reflujo después de una manga a menudo está relacionado con una hernia hiatal, que puede
          repararse en la misma operación — vea{" "}
          <Link to="/es/reparacion-de-hernia-hiatal" className="text-brand-deep underline hover:no-underline">reparación de hernia hiatal</Link>.
        </p>
      </EsSection>

      <EsSection id="por-que" heading="Por qué se recupera el peso, y por qué no es su culpa">
        <p>
          Dígamoslo con claridad: la recuperación de peso después de la cirugía bariátrica casi
          siempre se debe a la biología, no a la falta de voluntad. Cuando el cuerpo pierde peso, se
          defiende: suben las hormonas del apetito, bajan las señales de saciedad y se queman menos
          calorías en reposo. Esa defensa es coordinada y poderosa, y no es algo que se resuelva solo
          con decisión.
        </p>
        <p>
          Las operaciones también cambian con el tiempo. Una manga puede estirarse y dilatarse. Una
          bolsa gástrica o su salida pueden agrandarse después de un bypass. Una banda ajustable
          puede deslizarse o erosionarse. Y a veces la primera operación simplemente nunca produjo
          suficiente pérdida de peso. Con frecuencia se evalúa una revisión por: pérdida de peso que
          nunca alcanzó un nivel saludable, recuperación de peso después de un buen resultado inicial
          (a menudo años después), el regreso de condiciones como{" "}
          <Link to="/es/obesidad-y-diabetes" className="font-medium text-brand-logo hover:underline">diabetes tipo 2</Link>, presión alta o
          apnea del sueño, reflujo ácido grave después de una manga gástrica que el medicamento no
          controla, o un problema mecánico con el procedimiento original, como una banda deslizada o
          erosionada, una estenosis o una fístula.
        </p>
      </EsSection>

      <EsSection id="opciones" heading="Opciones de revisión">
        <p>
          La atención de revisión cubre dos enfoques amplios. Los procedimientos endoscópicos no
          usan incisiones y se hacen por la boca. Los procedimientos quirúrgicos se realizan por
          laparoscopia. El Dr. Fitzer recomienda un camino según su primera operación, su anatomía
          actual, sus metas y el balance entre riesgo y beneficio para usted.
        </p>
        <p>
          <strong>Revisión endoscópica.</strong> Algunos pacientes recuperan peso después de un
          bypass gástrico porque la bolsa o su salida se dilataron. Para ellos, un procedimiento
          llamado <Link to="/es/cirugia-de-revision/tore" className="font-medium text-brand-logo hover:underline">reducción transoral de la salida (TORe)</Link> puede ajustar la conexión con suturas
          colocadas por la boca, sin incisiones. Estos procedimientos tienen menor riesgo y
          recuperación rápida; producen una pérdida de peso real a corto plazo, aunque el efecto es
          más modesto y menos duradero que el de una revisión quirúrgica.
        </p>
      </EsSection>

      <EsSection id="revision-manga" heading="Revisión de la manga gástrica">
        <p>
          La{" "}
          <Link to="/es/manga-gastrica" className="font-medium text-brand-logo hover:underline">
            manga gástrica
          </Link>{" "}
          es la operación inicial más común en el mundo, y también la que más se revisa, ya sea por
          pérdida de peso insuficiente o por reflujo. La conversión mejor establecida es hacia un{" "}
          <Link to="/es/bypass-gastrico" className="font-medium text-brand-logo hover:underline">
            bypass gástrico
          </Link>{" "}
          en Y de Roux, que agrega un efecto de malabsorción y es muy eficaz contra el reflujo. Los
          datos combinados muestran que la conversión de manga a bypass produce alrededor de un 20
          por ciento de pérdida de peso total y cerca de un 55 por ciento de pérdida de exceso de
          peso al año, con remisión de la diabetes en aproximadamente la mitad de los pacientes. Si
          necesita mayor pérdida de peso, se puede considerar un switch duodenal o un bypass
          duodenoileal de una sola anastomosis (SADI); ambos requieren seguimiento nutricional
          estrecho de por vida.
        </p>
        <p>
          Cuando la manga se ha dilatado claramente y el reflujo no es un problema, a veces se puede
          volver a hacer la manga («re-manga») en lugar de convertirla. Si hay reflujo presente, la
          conversión a bypass gástrico suele ser la mejor opción, porque la re-manga puede empeorar
          el reflujo.
        </p>
        <p className="mt-3">
          <Link
            to="/es/cirugia-de-revision/manga-gastrica"
            className="font-medium text-brand-logo hover:underline"
          >
            Más sobre la cirugía de revisión de la manga gástrica
          </Link>
        </p>
      </EsSection>

      <EsSection id="revision-bypass" heading="Opciones de revisión del bypass gástrico">
        <p>
          Cuando el peso regresa después de un{" "}
          <Link to="/es/bypass-gastrico" className="font-medium text-brand-logo hover:underline">
            bypass gástrico
          </Link>
          , hay tres opciones principales: la <Link to="/es/cirugia-de-revision/tore" className="font-medium text-brand-logo hover:underline">reducción endoscópica de la salida (TORe)</Link>, descrita
          antes; la revisión quirúrgica de la bolsa gástrica misma; y la distalización, en la que se
          alarga el segmento desviado del intestino delgado para activar la malabsorción. La
          revisión se planea según su anatomía y su razón de recuperación de peso, no según una
          operación única por defecto.
        </p>

        <figure className="mt-6" aria-label="Foto laparoscópica antes de una revisión de la bolsa gástrica">
          <picture>
            <source
              type="image/webp"
              srcSet={`${pouchRevisionWebp800.url} 800w, ${pouchRevisionWebp1200.url} 1200w, ${pouchRevisionWebp1600.url} 1600w`}
              sizes="(min-width: 1024px) 800px, 100vw"
            />
            <img
              src={pouchRevisionJpg800.url}
              srcSet={`${pouchRevisionJpg800.url} 800w, ${pouchRevisionJpg1200.url} 1200w, ${pouchRevisionJpg1600.url} 1600w`}
              sizes="(min-width: 1024px) 800px, 100vw"
              width={1600}
              height={890}
              alt={OG_IMAGE_ALT}
              aria-describedby="revision-caption-es"
              className="h-auto w-full rounded-lg bg-muted bg-cover bg-center"
              style={{
                backgroundImage:
                  "url(data:image/webp;base64,UklGRn4AAABXRUJQVlA4IHIAAACwBACdASoUAAsAPu1iqU2ppaOiMAgBMB2JbACdMoRwACxjkh7YAJR/fGTl5UkAAP7sx0s7eA/mu6p2WLiu5S0HzZs9/Gl0obBXdr+EgiGfxFR1hasG5N03wO8q+Q/MC7N2s9Xkj53Jl9rLnHuqIC1gAAA=)",
              }}
              loading="lazy"
              fetchPriority="low"
              decoding="async"
            />
          </picture>
          <figcaption id="revision-caption-es" className="mt-2 text-center text-sm text-foreground/70">
            {REVISION_IMAGE_CAPTION}
          </figcaption>
        </figure>

        <p className="mt-3">
          <Link
            to="/es/cirugia-de-revision/bypass-gastrico"
            className="font-medium text-brand-logo hover:underline"
          >
            Más sobre la cirugía de revisión del bypass gástrico
          </Link>
        </p>
      </EsSection>

      <EsSection id="revision-banda" heading="Retiro y revisión de la banda gástrica">
        <p>
          Los cirujanos colocan bandas gástricas ajustables con mucha menos frecuencia hoy. Muchos
          pacientes con una{" "}
          <Link to="/es/banda-gastrica" className="font-medium text-brand-logo hover:underline">
            banda gástrica
          </Link>{" "}
          antigua terminan retirándola y convirtiéndola, casi siempre a una manga gástrica o a un
          bypass gástrico, para obtener resultados más confiables a largo plazo. Una banda que se ha
          deslizado, erosionado o causado dificultad para tragar debe evaluarse pronto. Cuando el
          tejido alrededor de la banda está sano, el Dr. Fitzer puede retirarla y hacer la conversión
          en una sola operación laparoscópica. Si hay inflamación o erosión significativa, primero se
          retira la banda y la conversión se hace unos meses después. La mayoría de los pacientes
          recuperan peso si solo se retira la banda sin conversión, porque no queda nada que limite
          la ingesta.
        </p>
        <p className="mt-3">
          <Link
            to="/es/cirugia-de-revision/banda-gastrica"
            className="font-medium text-brand-logo hover:underline"
          >
            Más sobre el retiro y la conversión de la banda gástrica
          </Link>
        </p>
      </EsSection>

      <EsSection id="seguro" heading="Cómo lograr que el seguro cubra la revisión">
        <p>
          La mayoría de los planes sí cubren la cirugía de revisión, pero piden más documentación
          que para una primera operación. Las aseguradoras generalmente quieren prueba de un
          problema mecánico o médico con el procedimiento original, o evidencia de que usted sigue
          cumpliendo sus criterios de peso y salud. Ayuda tener el reporte operatorio original,
          imágenes o endoscopia que muestren su anatomía actual, un historial de peso documentado y
          los registros del reflujo, la diabetes u otras condiciones que hayan regresado.
        </p>
        <p>
          Nuestro consultorio se encarga del papeleo y de la autorización previa con usted. Consulte
          también nuestra página de{" "}
          <Link to="/es/cobertura-de-seguro" className="font-medium text-brand-logo hover:underline">
            cobertura de seguro
          </Link>{" "}
          para conocer lo que suelen exigir las principales aseguradoras en Virginia.
        </p>
      </EsSection>

      <EsSection id="costo-pago-propio" heading="Costo con pago propio">
        <p>
          ¿No va a usar el seguro? Consulte el{" "}
          <Link
            to="/es/cirugia-bariatrica-de-pago-directo"
            hash="costo-cirugia-de-revision"
            className="font-medium text-brand-logo hover:underline"
          >
            costo de la cirugía de revisión
          </Link>{" "}
          con pago propio: un estimado de $25,400, que cubre el honorario del cirujano, un día de
          hospital en INOVA Fair Oaks Hospital, un estimado de anestesia y un año de seguimiento
          de rutina con el Dr. Fitzer.
        </p>
      </EsSection>

      <EsSection id="pacientes-de-virginia" heading="Cirugía bariátrica de revisión para pacientes de toda Virginia">
        <p>
          Las revisiones atraen a pacientes de mucho más allá del norte de Virginia, como Richmond, Charlottesville, Fredericksburg y Hampton Roads. Muchos comienzan con una consulta por telesalud. Las revisiones endoscópicas como el TORe son ambulatorias, y las revisiones quirúrgicas suelen requerir una noche en INOVA Fair Oaks Hospital en Fairfax. Planificamos las visitas para que quienes viajan hagan el menor número posible de viajes a Herndon.
        </p>
        <p>
          Como hay relativamente pocos especialistas en cirugía de revisión, muchos pacientes viajan desde lugares mucho más lejanos. Atendemos a muchos pacientes de Richmond, Charlottesville, Fredericksburg, Newport News, Norfolk, Virginia Beach y Suffolk. Diseñamos nuestro proceso para facilitarles la atención a quienes vienen de fuera: consultas iniciales y visitas de seguimiento por telesalud, citas preoperatorias agrupadas y seguimiento por telesalud o programado según sus viajes.
        </p>
        <p>
          ¿Preguntas sobre la cirugía de revisión? Llame a nuestro consultorio de Herndon al{" "}
          <a href="tel:+17037099771" aria-label="Llame a Virginia Bariatric Surgery al 703-709-9771" className="font-medium text-brand-logo hover:underline">
            703-709-9771
          </a>
          .
        </p>
        <SectionCta to="/es/contacto" label="cta_revisions_hub_location_es">Solicite una Consulta</SectionCta>
      </EsSection>

      <EsSection id="preguntas-frecuentes" heading="Preguntas frecuentes">
        <dl className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={f.q} id={`faq-${i + 1}`}>
              <dt className="font-medium text-brand-deep">{f.q}</dt>
              <dd className="mt-1">{f.a}</dd>
            </div>
          ))}
        </dl>
      </EsSection>
    </EsPageShell>
  );
}
